import { zodResolver } from '@hookform/resolvers/zod';
import { Action, TextInput } from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { FormEventHandler } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useDiscountDefinitionCreate } from '~/features/settings/hooks/useDiscountDefinitions';
import { useCurrency } from '~/hooks/useCurrency';
import { CombinationsFieldset } from './CombinationsFieldset';
import { DiscountValueField } from './DiscountValueField';
import { MinimumRequirementFieldset } from './MinimumRequirementFieldset';

const createSchema = z.object({
  title: z.string(),
  discountDefinition: z.object({
    mutation: z.literal('discountCodeBasicCreate'),
    parameters: CrewMerchantUi.amountOffOrderRawSchema,
  }),
});
type FormValues = z.infer<typeof createSchema>;

const defaults: FormValues = {
  title: 'Amount off Order Discount',
  discountDefinition: {
    mutation: 'discountCodeBasicCreate',
    parameters: {
      kind: 'amountOffOrder',
      combinesWith: {
        orderDiscounts: false,
        productDiscounts: false,
        shippingDiscounts: false,
      },
      customerGets: {
        value: { percentage: 10 },
      },
    },
  },
};

export function AmountOffOrderForm({ onClose }: { onClose: () => void }) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: defaults,
  });

  const { mutate: createDiscountDefinition } = useDiscountDefinitionCreate();
  const { currencyCode, currencySymbol } = useCurrency();

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) => {
      createDiscountDefinition(values);
      reset();
      onClose();
    })(event).catch(console.error);
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={submitHandler}>
      <TextInput
        id="title"
        label="Title"
        details="The name of the discount in Corso."
        required
        {...register('title')}
        error={errors.title?.message}
      />

      <Controller
        control={control}
        name="discountDefinition.parameters.customerGets.value"
        render={({ field, fieldState }) => (
          <DiscountValueField
            value={field.value}
            onChange={field.onChange}
            currencyCode={currencyCode}
            currencySymbol={currencySymbol}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="discountDefinition.parameters.minimumRequirement"
        render={({ field }) => (
          <MinimumRequirementFieldset
            value={field.value}
            onChange={field.onChange}
            currencySymbol={currencySymbol}
          />
        )}
      />

      <Controller
        control={control}
        name="discountDefinition.parameters.combinesWith"
        render={({ field }) => (
          <CombinationsFieldset value={field.value} onChange={field.onChange} />
        )}
      />

      <Action variant="primary" type="submit">
        Create
      </Action>
    </form>
  );
}
